import { Injectable, Signal } from "@angular/core";
import {
  EbuCarModelResponse,
  PackageResponse,
  PackageTagEnum,
  PriceResponse,
  PriceTypeEnum,
  PropertyEnum,
  SelectedPackagesRequest,
  VehicleSearchResponse
} from "src/domain/client";
import { Store } from "@ngrx/store";
import {
  selectSelectedEbuCar,
  selectCarInfo
} from "../state/booking.selectors";

@Injectable({
  providedIn: "root"
})
export class PriceService {
  readonly selectedEbuCar: Signal<EbuCarModelResponse | null>;
  readonly carInfo: Signal<VehicleSearchResponse | null>;

  constructor(private store: Store) {
    this.selectedEbuCar = this.store.selectSignal(selectSelectedEbuCar);
    this.carInfo = this.store.selectSignal(selectCarInfo);
  }

  public determinePrice(price: PriceResponse): number {
    if (!price) return 0;
    const pricing = price.discounted ? price.discounted : price.price;
    return Math.floor(pricing);
  }

  public getPriceOfPackage(
    pkg: PackageResponse,
    selectedPackages: SelectedPackagesRequest[],
    includeSubpackages: boolean = true
  ): number {
    if (
      pkg.tags.includes(PackageTagEnum.ServiceAgreement) &&
      this.carInfo()?.serviceAgreementResponse?.isSuccess
    )
      return 0;
    const currentSelection = selectedPackages.find(
      (s) => (s.packageId?.toString() || s.packageExternalId) === pkg.id
    );
    if (!pkg.subPackages.length) return pkg.price.discounted;
    const packagePrice = this.determinePrice(pkg.price);
    const subPackagesPrice = pkg.subPackages
      .map((p) => {
        const isSelected = p.tags.includes(PackageTagEnum.Removable)
          ? currentSelection && currentSelection.subPackageIds.includes(p.id)
          : true;
        if (!isSelected) return 0;
        if (p.properties?.includes(PropertyEnum.EuropcarDropoff)) {
          const selectedCar = this.selectedEbuCar();
          if (selectedCar) {
            return Math.floor(selectedCar.previewValue);
          }
          return 0;
        }
        return this.determinePrice(p.price);
      })
      .reduce((a, b) => a + b, 0);
    return includeSubpackages ? subPackagesPrice + packagePrice : packagePrice;
  }

  public getNonDiscountedPriceOfPackage(
    pkg: PackageResponse,
    selectedPackages: SelectedPackagesRequest[]
  ): number {
    const currentSelection = selectedPackages.find(
      (s) => (s.packageId?.toString() || s.packageExternalId) === pkg.id
    );
    if (!pkg.subPackages.length) return pkg.price.price;
    return pkg.subPackages
      .map((p) => {
        return p.tags.includes(PackageTagEnum.Removable)
          ? !!currentSelection && currentSelection.subPackageIds.includes(p.id)
            ? Math.floor(p.price?.price)
            : 0
          : Math.floor(p.price?.price);
      })
      .reduce((a, b) => a + b);
  }

  public determinePriceType(pkg: PackageResponse): PriceTypeEnum {
    const numberOfOffer = pkg.subPackages.filter(
      (p) => p.price.type === PriceTypeEnum.Offer
    ).length;
    if (!!numberOfOffer || pkg.price?.type == PriceTypeEnum.Offer)
      return PriceTypeEnum.Offer;
    const numberOfDeduct = pkg.subPackages.filter(
      (p) => p.price.type === PriceTypeEnum.Deductibles
    ).length;
    if (!!numberOfDeduct || pkg.price?.type == PriceTypeEnum.Deductibles) {
      return PriceTypeEnum.Deductibles;
    }
    const noFr = pkg.subPackages.filter(
      (p) => p.price.type === PriceTypeEnum.From
    ).length;
    if (!!noFr || pkg.price?.type == PriceTypeEnum.From) {
      return PriceTypeEnum.From;
    }
    return PriceTypeEnum.Fixed;
  }
}
